// src/components/automation/TemplateGallery.tsx
import React from 'react';
import { AutomationTemplate } from '../../types/automation.types';
import {
  Users,
  Calendar,
  AlertTriangle,
  Bell,
  Clock,
  Zap,
  Plus
} from 'lucide-react';

interface TemplateGalleryProps {
  templates: AutomationTemplate[];
  onSelect: (template: AutomationTemplate) => void;
}

export const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  templates,
  onSelect
}) => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'lead':
        return <Users className="h-6 w-6" />;
      case 'appointment':
        return <Calendar className="h-6 w-6" />;
      case 'alert':
        return <AlertTriangle className="h-6 w-6" />;
      case 'follow_up':
        return <Bell className="h-6 w-6" />;
      default:
        return <Zap className="h-6 w-6" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'lead':
        return 'bg-secondary-100 text-secondary-600 border-secondary-200';
      case 'appointment':
        return 'bg-primary-100 text-primary-600 border-primary-200';
      case 'alert':
        return 'bg-coral-100 text-coral-500 border-coral-200';
      case 'follow_up':
        return 'bg-accent-100 text-accent-600 border-accent-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getTriggerDescription = (template: AutomationTemplate) => {
    if (template.trigger.type === 'scheduled') {
      const schedule = template.trigger.schedule;
      if (schedule?.type === 'relative') {
        return `Scheduled: ${Math.abs(schedule.offsetDays || 0)} day${Math.abs(schedule.offsetDays || 0) !== 1 ? 's' : ''} ${(schedule.offsetDays || 0) < 0 ? 'before' : 'after'} ${schedule.relativeTo?.replace('_', ' ')}`;
      }
      return `Scheduled: Daily at ${schedule?.time || '09:00'}`;
    }

    const eventLabels: Record<string, string> = {
      'lead_created': 'When lead is created',
      'appointment_created': 'When appointment is created',
      'appointment_status_changed': 'When appointment status changes',
      'lead_status_changed': 'When lead status changes'
    };
    return eventLabels[template.trigger.event || ''] || 'Event-based trigger';
  };

  const getActionsDescription = (template: AutomationTemplate) => {
    const actions = template.actions.map(a => {
      if (a.type === 'create_task') return 'Creates task';
      if (a.type === 'send_notification') return 'Sends notification';
      if (a.type === 'send_email') return 'Sends email';
      return 'Action';
    });
    return actions.join(' & ');
  };

  // Group templates by category
  const groupedTemplates = templates.reduce((acc, template) => {
    const category = template.category;
    if (!acc[category]) acc[category] = [];
    acc[category].push(template);
    return acc;
  }, {} as Record<string, AutomationTemplate[]>);

  const categoryLabels: Record<string, string> = {
    lead: 'Lead Management',
    appointment: 'Appointments',
    alert: 'Alerts & Notifications',
    follow_up: 'Follow-ups'
  };

  return (
    <div className="space-y-8">
      <p className="text-gray-600">
        Choose a template to quickly create an automation rule. You can customize the settings after selecting.
      </p>

      {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
        <div key={category}>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
            {categoryLabels[category] || category}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categoryTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white border-2 border-gray-100 rounded-xl p-5 hover:border-primary-200 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => onSelect(template)}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl border ${getCategoryColor(template.category)}`}>
                    {getCategoryIcon(template.category)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {template.name}
                    </h4>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {template.description}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
                      <span className="flex items-center gap-1 text-gray-500">
                        {template.trigger.type === 'scheduled' ? (
                          <Clock className="h-3.5 w-3.5" />
                        ) : (
                          <Zap className="h-3.5 w-3.5" />
                        )}
                        {getTriggerDescription(template)}
                      </span>
                    </div>

                    <div className="text-xs text-gray-400 mt-2">
                      {getActionsDescription(template)}
                    </div>
                  </div>

                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="p-2 bg-primary-50 text-primary-600 rounded-lg">
                      <Plus className="h-5 w-5" />
                    </div>
                  </div>
                </div>

                {template.configurableFields.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-400">
                      {template.configurableFields.length} customizable setting{template.configurableFields.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TemplateGallery;
